import { useState, useEffect, useCallback } from "react";
import { X, Link, Copy, Check } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useStore } from "../../store/useStore";
import type { ITag } from "../../types";

interface TagSharePanelProps {
  tag: ITag;
}

export default function TagSharePanel({ tag }: TagSharePanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shares, setShares] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [loaded, setLoaded] = useState(false);
  const generateTagShareLink = useStore((s) => s.generateTagShareLink);
  const revokeTagShare = useStore((s) => s.revokeTagShare);

  const loadShares = useCallback(async () => {
    if (loaded) return;
    const { data } = await supabase
      .from("tag_shares")
      .select("id, shared_with_uuid")
      .eq("tag_id", tag.id);

    if (data && data.length > 0) {
      const uuids = data.map((s) => s.shared_with_uuid);
      const { data: users } = await supabase
        .from("users")
        .select("uuid, name, email")
        .in("uuid", uuids);

      const userMap = new Map((users || []).map((u) => [u.uuid, u]));
      setShares(
        data.map((s) => {
          const u = userMap.get(s.shared_with_uuid);
          return { id: s.id, name: u?.name || "Unknown", email: u?.email || "" };
        })
      );
    }
    setLoaded(true);
  }, [loaded, tag.id]);

  const handleGenerateLink = async () => {
    setLoading(true);
    setError(null);
    setInviteUrl(null);
    setCopied(false);

    const result = await generateTagShareLink(tag.id);
    if (result.success && result.invite_url) {
      setInviteUrl(result.invite_url);
    } else {
      setError(result.error || "Failed to generate link");
    }
    setLoading(false);
  };

  const handleCopy = async () => {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async (shareId: string) => {
    await revokeTagShare(shareId);
    setShares(shares.filter((s) => s.id !== shareId));
  };

  useEffect(() => {
    if (!loaded) loadShares();
  }, [loaded, loadShares]);

  return (
    <div className="mt-2 pl-4 border-l-2 border-border space-y-2">
      {/* Current shares */}
      {shares.map((share) => (
        <div key={share.id} className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground truncate">
            {share.name} ({share.email})
          </span>
          <button
            onClick={() => handleRevoke(share.id)}
            className="p-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}

      {/* Generate invite link */}
      {inviteUrl ? (
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={inviteUrl}
            className="flex-1 rounded border border-input bg-background px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <button
            onClick={handleCopy}
            className="p-1 text-primary hover:text-primary/80 flex-shrink-0"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      ) : (
        <button
          onClick={handleGenerateLink}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 disabled:opacity-50"
        >
          <Link className="w-3.5 h-3.5" />
          {loading ? "Generating..." : "Generate Invite Link"}
        </button>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
